import { useEffect, useRef } from "react";
import { createChart, ColorType, CrosshairMode, LineStyle } from "lightweight-charts";
import {
  sma, ema, bollinger, vwap, parabolicSar, ichimoku,
  rsi, macd, stochastic, cci, adx, atr,
} from "../lib/indicators";

const UP = "#10B981";
const DOWN = "#EF4444";
const GRID = "rgba(255,255,255,0.04)";

function heikinAshi(candles) {
  const out = [];
  let prevOpen, prevClose;
  for (const c of candles) {
    const haClose = (c.open + c.high + c.low + c.close) / 4;
    const haOpen = prevOpen == null ? (c.open + c.close) / 2 : (prevOpen + prevClose) / 2;
    const haHigh = Math.max(c.high, haOpen, haClose);
    const haLow = Math.min(c.low, haOpen, haClose);
    out.push({ time: c.time, open: haOpen, high: haHigh, low: haLow, close: haClose });
    prevOpen = haOpen; prevClose = haClose;
  }
  return out;
}

export default function PriceChart({ candles, chartType = "candlestick", overlays = [], oscillator = null, height = 420 }) {
  const containerRef = useRef(null);
  const oscRef = useRef(null);
  const chartRef = useRef(null);
  const oscChartRef = useRef(null);
  const mainSeriesRef = useRef(null);
  const overlaySeriesRef = useRef([]);
  const oscSeriesRef = useRef([]);
  const candlesRef = useRef(candles);
  candlesRef.current = candles;

  // create charts once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "#0A0B0E" }, textColor: "#94A3B8", fontFamily: "JetBrains Mono, monospace" },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)" },
      timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true, secondsVisible: true },
      width: containerRef.current.clientWidth,
      height,
    });
    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth });
      if (oscRef.current && oscChartRef.current) oscChartRef.current.applyOptions({ width: oscRef.current.clientWidth });
    });
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, [height]);

  // (re)build main + overlay series when type/overlays change
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (mainSeriesRef.current) { try { chart.removeSeries(mainSeriesRef.current); } catch {} mainSeriesRef.current = null; }
    overlaySeriesRef.current.forEach((s) => { try { chart.removeSeries(s); } catch {} });
    overlaySeriesRef.current = [];

    let main;
    if (chartType === "line") main = chart.addLineSeries({ color: "#3B82F6", lineWidth: 2 });
    else if (chartType === "area") main = chart.addAreaSeries({ lineColor: "#3B82F6", topColor: "rgba(59,130,246,0.4)", bottomColor: "rgba(59,130,246,0.02)", lineWidth: 2 });
    else if (chartType === "bar") main = chart.addBarSeries({ upColor: UP, downColor: DOWN });
    else main = chart.addCandlestickSeries({ upColor: UP, downColor: DOWN, borderUpColor: UP, borderDownColor: DOWN, wickUpColor: UP, wickDownColor: DOWN });
    mainSeriesRef.current = main;

    const addLine = (color, width = 1, style = LineStyle.Solid) => {
      const s = chart.addLineSeries({ color, lineWidth: width, lineStyle: style, priceLineVisible: false, lastValueVisible: false });
      overlaySeriesRef.current.push(s);
      return s;
    };
    const meta = {};
    if (overlays.includes("MA")) meta.MA = addLine("#F59E0B", 2);
    if (overlays.includes("EMA")) meta.EMA = addLine("#3B82F6", 2);
    if (overlays.includes("BB")) { meta.BBU = addLine("#64748B"); meta.BBM = addLine("#94A3B8", 1, LineStyle.Dashed); meta.BBL = addLine("#64748B"); }
    if (overlays.includes("VWAP")) meta.VWAP = addLine("#A855F7", 2);
    if (overlays.includes("SAR")) meta.SAR = addLine("#EC4899", 1, LineStyle.Dotted);
    if (overlays.includes("ICHIMOKU")) { meta.ICONV = addLine("#22D3EE", 2); meta.IBASE = addLine("#F97316", 2); }
    overlayMeta.current = meta;

    pushMainData();
    pushOverlayData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType, overlays.join(",")]);

  const overlayMeta = useRef({});
  const oscMeta = useRef({});

  // build oscillator pane
  useEffect(() => {
    if (!oscRef.current) return;
    if (oscChartRef.current) { oscChartRef.current.remove(); oscChartRef.current = null; oscSeriesRef.current = []; }
    if (!oscillator) return;
    const chart = createChart(oscRef.current, {
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "#0A0B0E" }, textColor: "#64748B", fontFamily: "JetBrains Mono, monospace" },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)" },
      timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true, secondsVisible: true },
      width: oscRef.current.clientWidth,
      height: 140,
    });
    oscChartRef.current = chart;
    const meta = {};
    if (oscillator === "MACD") {
      meta.HIST = chart.addHistogramSeries({ color: "#3B82F6" });
      meta.LINE = chart.addLineSeries({ color: "#F59E0B", lineWidth: 1, lastValueVisible: false });
      meta.SIG = chart.addLineSeries({ color: "#EF4444", lineWidth: 1, lastValueVisible: false });
    } else if (oscillator === "VOL") {
      meta.MAIN = chart.addHistogramSeries({ color: "#3B82F6" });
    } else {
      meta.MAIN = chart.addLineSeries({ color: "#3B82F6", lineWidth: 2 });
    }
    oscMeta.current = meta;
    pushOscData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oscillator]);

  function pushMainData() {
    const c = candlesRef.current;
    const s = mainSeriesRef.current;
    if (!s || !c?.length) return;
    if (chartType === "line" || chartType === "area") {
      s.setData(c.map((x) => ({ time: x.time, value: x.close })));
    } else if (chartType === "heikin") {
      s.setData(heikinAshi(c));
    } else {
      s.setData(c.map((x) => ({ time: x.time, open: x.open, high: x.high, low: x.low, close: x.close })));
    }
  }

  function pushOverlayData() {
    const c = candlesRef.current;
    const m = overlayMeta.current;
    if (!c?.length) return;
    if (m.MA) m.MA.setData(sma(c, 20));
    if (m.EMA) m.EMA.setData(ema(c, 20));
    if (m.BBU) { const bb = bollinger(c, 20, 2); m.BBU.setData(bb.upper); m.BBM.setData(bb.mid); m.BBL.setData(bb.lower); }
    if (m.VWAP) m.VWAP.setData(vwap(c));
    if (m.SAR) m.SAR.setData(parabolicSar(c));
    if (m.ICONV) { const ic = ichimoku(c); m.ICONV.setData(ic.conv); m.IBASE.setData(ic.base); }
  }

  function pushOscData() {
    const c = candlesRef.current;
    const m = oscMeta.current;
    if (!c?.length || !oscillator) return;
    if (oscillator === "RSI") m.MAIN?.setData(rsi(c, 14));
    else if (oscillator === "STOCH") m.MAIN?.setData(stochastic(c, 14));
    else if (oscillator === "CCI") m.MAIN?.setData(cci(c, 20));
    else if (oscillator === "ADX") m.MAIN?.setData(adx(c, 14));
    else if (oscillator === "ATR") m.MAIN?.setData(atr(c, 14));
    else if (oscillator === "VOL") m.MAIN?.setData(c.map((x) => ({ time: x.time, value: x.volume, color: x.close >= x.open ? "rgba(16,185,129,0.6)" : "rgba(239,68,68,0.6)" })));
    else if (oscillator === "MACD") { const md = macd(c); m.HIST?.setData(md.hist.map((h) => ({ ...h, color: h.value >= 0 ? "rgba(16,185,129,0.6)" : "rgba(239,68,68,0.6)" }))); m.LINE?.setData(md.line); m.SIG?.setData(md.signalLine); }
  }

  // update data when candles change
  useEffect(() => { pushMainData(); pushOverlayData(); pushOscData(); /* eslint-disable-next-line */ }, [candles]);

  return (
    <div className="w-full">
      <div ref={containerRef} data-testid="price-chart" className="w-full" style={{ height }} />
      {oscillator && <div ref={oscRef} data-testid="oscillator-pane" className="w-full mt-1 border-t border-white/5" style={{ height: 140 }} />}
    </div>
  );
}
